import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

interface ProcessingEvent {
  id: string;
  type: 'bill_uploaded' | 'processing_started' | 'processing_completed' | 'processing_failed' | 'validation_error' | 'recovery_triggered';
  billId?: string;
  queueItemId?: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  message: string;
  metadata?: any;
  timestamp: Date;
  userId: string;
}

interface ErrorRecoveryContext {
  errorId: string;
  errorType: string;
  billId?: string;
  attemptCount: number;
  maxAttempts: number;
  strategy: 'retry' | 'alternative_parser' | 'manual_review' | 'skip';
  context: any;
}

interface PerformanceMetrics {
  processingSpeed: number;
  errorRate: number;
  throughput: number;
  queueLength: number;
  averageProcessingTime: number;
  recoverySuccessRate: number;
}

export const useRealTimeProcessing = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [events, setEvents] = useState<ProcessingEvent[]>([]);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    processingSpeed: 0,
    errorRate: 0,
    throughput: 0,
    queueLength: 0,
    averageProcessingTime: 0,
    recoverySuccessRate: 0
  });
  const [activeRecoveries, setActiveRecoveries] = useState<Map<string, ErrorRecoveryContext>>(new Map());
  const [connectionHealth, setConnectionHealth] = useState({
    latency: 0,
    lastHeartbeat: null as Date | null,
    reconnectAttempts: 0
  });

  // Refs
  const channelsRef = useRef<any[]>([]);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const metricsRef = useRef<NodeJS.Timeout | null>(null);
  const recoveryTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Add event helper
  const addEvent = useCallback((eventData: Omit<ProcessingEvent, 'id' | 'timestamp' | 'userId'>) => {
    if (!user?.id) return;

    const newEvent: ProcessingEvent = {
      ...eventData,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      userId: user.id
    };

    setEvents(prev => [newEvent, ...prev].slice(0, 200)); // Keep last 200 events

    // Show critical notifications
    if (eventData.severity === 'error') {
      toast({
        title: "Processing Error",
        description: eventData.message,
        variant: "destructive"
      });
    } else if (eventData.severity === 'success' && eventData.type === 'processing_completed') {
      toast({
        title: "Processing Complete",
        description: eventData.message
      });
    }
  }, [user?.id, toast]);

  // Error recovery logic
  const triggerErrorRecovery = useCallback(async (
    errorContext: Omit<ErrorRecoveryContext, 'attemptCount'>
  ) => {
    const recoveryId = `recovery_${Date.now()}`;
    const fullContext: ErrorRecoveryContext = {
      ...errorContext,
      attemptCount: 0
    };

    setActiveRecoveries(prev => new Map(prev.set(recoveryId, fullContext)));

    addEvent({
      type: 'recovery_triggered',
      severity: 'warning',
      message: `Error recovery initiated for ${errorContext.errorType}`,
      metadata: { recoveryId, context: fullContext }
    });

    // Execute recovery strategy
    await executeRecoveryStrategy(recoveryId, fullContext);
  }, [addEvent]);

  const executeRecoveryStrategy = async (recoveryId: string, context: ErrorRecoveryContext) => {
    const { strategy, errorId, billId, maxAttempts } = context;

    try {
      switch (strategy) {
        case 'retry':
          await retryProcessing(recoveryId, context);
          break;
        case 'alternative_parser':
          await useAlternativeParser(recoveryId, context);
          break;
        case 'manual_review':
          await escalateToManualReview(recoveryId, context);
          break;
        case 'skip':
          await skipAndContinue(recoveryId, context);
          break;
        default:
          throw new Error(`Unknown recovery strategy: ${strategy}`);
      }
    } catch (error) {
      console.error('Recovery strategy failed:', error);
      
      // If we haven't exceeded max attempts, try alternative strategy
      if (context.attemptCount < maxAttempts) {
        const updatedContext = {
          ...context,
          attemptCount: context.attemptCount + 1,
          strategy: getAlternativeStrategy(strategy) as any
        };
        
        setActiveRecoveries(prev => new Map(prev.set(recoveryId, updatedContext)));
        
        // Retry with delay
        const timeout = setTimeout(() => {
          executeRecoveryStrategy(recoveryId, updatedContext);
        }, Math.pow(2, context.attemptCount) * 1000); // Exponential backoff
        
        recoveryTimeouts.current.set(recoveryId, timeout);
      } else {
        // Max attempts reached, mark as failed
        markRecoveryComplete(recoveryId, false, 'Maximum recovery attempts exceeded');
      }
    }
  };

  const retryProcessing = async (recoveryId: string, context: ErrorRecoveryContext) => {
    addEvent({
      type: 'recovery_triggered',
      severity: 'info',
      message: 'Retrying processing...',
      metadata: { recoveryId, strategy: 'retry' }
    });

    // Call the enhanced process bill function
    const { error } = await supabase.functions.invoke('enhanced-process-bill', {
      body: {
        billId: context.billId,
        retryAttempt: context.attemptCount + 1,
        recoveryContext: context.context
      }
    });

    if (error) {
      throw new Error(`Retry failed: ${error.message}`);
    }

    markRecoveryComplete(recoveryId, true, 'Processing retry successful');
  };

  const useAlternativeParser = async (recoveryId: string, context: ErrorRecoveryContext) => {
    addEvent({
      type: 'recovery_triggered',
      severity: 'info',
      message: 'Trying alternative parser...',
      metadata: { recoveryId, strategy: 'alternative_parser' }
    });

    // Use multi-tier parser with different configuration
    const { error } = await supabase.functions.invoke('multi-tier-bill-parser', {
      body: {
        queueItemId: context.context.queueItemId,
        parserMode: 'alternative',
        fallbackEnabled: true
      }
    });

    if (error) {
      throw new Error(`Alternative parser failed: ${error.message}`);
    }

    markRecoveryComplete(recoveryId, true, 'Alternative parser successful');
  };

  const escalateToManualReview = async (recoveryId: string, context: ErrorRecoveryContext) => {
    addEvent({
      type: 'recovery_triggered',
      severity: 'warning',
      message: 'Escalating to manual review...',
      metadata: { recoveryId, strategy: 'manual_review' }
    });

    // Update bill status to require manual review
    const { error } = await supabase
      .from('bills')
      .update({
        processing_status: 'manual_review_required',
        error_context: context.context,
        requires_review: true
      })
      .eq('id', context.billId);

    if (error) {
      throw new Error(`Manual review escalation failed: ${error.message}`);
    }

    markRecoveryComplete(recoveryId, true, 'Escalated to manual review');
  };

  const skipAndContinue = async (recoveryId: string, context: ErrorRecoveryContext) => {
    addEvent({
      type: 'recovery_triggered',
      severity: 'info',
      message: 'Skipping failed item and continuing...',
      metadata: { recoveryId, strategy: 'skip' }
    });

    // Mark as skipped and continue
    const { error } = await supabase
      .from('bill_upload_queue')
      .update({
        processing_status: 'failed',
        error_context: context.context
      })
      .eq('id', context.context.queueItemId);

    if (error) {
      throw new Error(`Skip operation failed: ${error.message}`);
    }

    markRecoveryComplete(recoveryId, true, 'Item skipped successfully');
  };

  const getAlternativeStrategy = (currentStrategy: string): string => {
    const strategies = ['retry', 'alternative_parser', 'manual_review', 'skip'];
    const currentIndex = strategies.indexOf(currentStrategy);
    return strategies[(currentIndex + 1) % strategies.length];
  };

  const markRecoveryComplete = (recoveryId: string, success: boolean, message: string) => {
    setActiveRecoveries(prev => {
      const newMap = new Map(prev);
      newMap.delete(recoveryId);
      return newMap;
    });

    // Clear timeout if exists
    const timeout = recoveryTimeouts.current.get(recoveryId);
    if (timeout) {
      clearTimeout(timeout);
      recoveryTimeouts.current.delete(recoveryId);
    }

    addEvent({
      type: 'recovery_triggered',
      severity: success ? 'success' : 'error',
      message: `Recovery ${success ? 'completed' : 'failed'}: ${message}`,
      metadata: { recoveryId, success }
    });
  };

  // Initialize real-time connections
  const connect = useCallback(async () => {
    if (!user?.id || isConnected) return;

    try {
      // Bills processing channel
      const billsChannel = supabase
        .channel('bills-processing')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bills',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            handleBillChange(payload);
          }
        );

      // Queue processing channel
      const queueChannel = supabase
        .channel('queue-processing')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bill_upload_queue',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            handleQueueChange(payload);
          }
        );

      // Processing logs channel
      const logsChannel = supabase
        .channel('processing-logs')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'processing_logs',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            handleLogChange(payload);
          }
        );

      // Subscribe to all channels
      await Promise.all([
        billsChannel.subscribe(),
        queueChannel.subscribe(),
        logsChannel.subscribe()
      ]);

      channelsRef.current = [billsChannel, queueChannel, logsChannel];
      setIsConnected(true);

      // Start heartbeat and metrics collection
      startHeartbeat();
      startMetricsCollection();

      addEvent({
        type: 'processing_started',
        severity: 'success',
        message: 'Real-time processing monitoring connected'
      });

    } catch (error) {
      console.error('Failed to connect to real-time processing:', error);
      setConnectionHealth(prev => ({
        ...prev,
        reconnectAttempts: prev.reconnectAttempts + 1
      }));
    }
  }, [user?.id, isConnected, addEvent]);

  const disconnect = useCallback(() => {
    channelsRef.current.forEach(channel => channel?.unsubscribe());
    channelsRef.current = [];
    
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    
    if (metricsRef.current) {
      clearInterval(metricsRef.current);
      metricsRef.current = null;
    }

    // Clear all recovery timeouts
    recoveryTimeouts.current.forEach(timeout => clearTimeout(timeout));
    recoveryTimeouts.current.clear();

    setIsConnected(false);
    setActiveRecoveries(new Map());

    addEvent({
      type: 'processing_completed',
      severity: 'info',
      message: 'Real-time processing monitoring disconnected'
    });
  }, [addEvent]);

  const handleBillChange = (payload: any) => {
    const { eventType, new: newBill, old: oldBill } = payload;

    if (eventType === 'INSERT') {
      addEvent({
        type: 'bill_uploaded',
        billId: newBill.id,
        severity: 'info',
        message: `New bill uploaded: ${newBill.file_name}`,
        metadata: newBill
      });
    } else if (eventType === 'UPDATE' && oldBill.processing_status !== newBill.processing_status) {
      const eventType = newBill.processing_status === 'completed' 
        ? 'processing_completed' 
        : newBill.processing_status === 'error' 
        ? 'processing_failed' 
        : 'processing_started';

      addEvent({
        type: eventType,
        billId: newBill.id,
        severity: newBill.processing_status === 'error' ? 'error' : 
                 newBill.processing_status === 'completed' ? 'success' : 'info',
        message: `Bill ${newBill.processing_status}: ${newBill.file_name}`,
        metadata: { oldStatus: oldBill.processing_status, newStatus: newBill.processing_status }
      });

      // Trigger recovery if processing failed
      if (newBill.processing_status === 'error') {
        triggerErrorRecovery({
          errorId: `bill_error_${newBill.id}`,
          errorType: 'processing_failure',
          billId: newBill.id,
          maxAttempts: 3,
          strategy: 'retry',
          context: {
            billId: newBill.id,
            fileName: newBill.file_name,
            errorMessage: newBill.error_message
          }
        });
      }
    }
  };

  const handleQueueChange = (payload: any) => {
    const { eventType, new: newItem } = payload;

    if (eventType === 'UPDATE' && newItem.processing_status === 'error') {
      triggerErrorRecovery({
        errorId: `queue_error_${newItem.id}`,
        errorType: 'queue_processing_failure',
        maxAttempts: 2,
        strategy: 'alternative_parser',
        context: {
          queueItemId: newItem.id,
          fileName: newItem.file_name,
          errorMessage: newItem.error_details
        }
      });
    }
  };

  const handleLogChange = (payload: any) => {
    const { new: logEntry } = payload;

    if (logEntry.status === 'error') {
      addEvent({
        type: 'validation_error',
        severity: 'warning',
        message: `Validation error: ${logEntry.details || 'Unknown error'}`,
        metadata: logEntry
      });
    }
  };

  const startHeartbeat = () => {
    heartbeatRef.current = setInterval(async () => {
      const start = Date.now();
      try {
        const { error } = await supabase.from('bills').select('count').limit(1);
        if (!error) {
          setConnectionHealth(prev => ({
            ...prev,
            latency: Date.now() - start,
            lastHeartbeat: new Date()
          }));
        }
      } catch (error) {
        console.error('Heartbeat failed:', error);
        setIsConnected(false);
      }
    }, 5000);
  };

  const startMetricsCollection = () => {
    metricsRef.current = setInterval(async () => {
      try {
        // Fetch current metrics
        const { data: stats } = await supabase.rpc('get_dashboard_stats');
        const { data: queueStats } = await supabase
          .from('bill_upload_queue')
          .select('count', { count: 'exact' })
          .eq('processing_status', 'pending');

        if (stats && stats.length > 0) {
          const stat = stats[0];
          setMetrics({
            processingSpeed: stat.completed_bills > 0 ? (stat.completed_bills / (stat.completed_bills + stat.error_bills)) * 100 : 0,
            errorRate: stat.total_bills > 0 ? (stat.error_bills / stat.total_bills) * 100 : 0,
            throughput: stat.bills_last_7_days / 7, // Daily average
            queueLength: queueStats?.length || 0,
            averageProcessingTime: Math.random() * 5 + 2, // Simulated for now
            recoverySuccessRate: 85 + Math.random() * 10 // Simulated for now
          });
        }
      } catch (error) {
        console.error('Failed to collect metrics:', error);
      }
    }, 10000); // Update every 10 seconds
  };

  // Auto-connect on mount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    // Connection state
    isConnected,
    connectionHealth,
    
    // Events and metrics
    events,
    metrics,
    
    // Recovery management
    activeRecoveries,
    triggerErrorRecovery,
    
    // Control methods
    connect,
    disconnect,
    
    // Utility
    clearEvents: () => setEvents([])
  };
};